import { useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '../../api/client'
import type { Media, Trip } from '../../api/types'
import { ensureSettings, useUploadMedia } from '../../hooks/queries'
import { prepareImage } from '../../lib/images'
import { PhotoIcon, TrashIcon } from '../../components/Icons'
import { rueckfrage } from '../../components/Rueckfrage'
import { Ausschnittwahl } from '../../components/Ausschnittwahl'
import { ausschnittAus, bildStil, type Ausschnitt } from '../../lib/ausschnitt'
import { useTexte } from '../../texte'

/**
 * Das Symbolbild einer Reise — in der Übersicht anstelle der Buchstaben.
 *
 * **Nur der Reisende**, wie bei allem, was den Bericht nach außen darstellt.
 * Der Server entscheidet das noch einmal; hier steht nur, was gar nicht erst
 * angeboten wird.
 *
 * Zwei Schritte, und der erste ist ein gewöhnlicher Upload: das Bild geht wie
 * jedes andere Foto in die Reise (verkleinert im Browser, gegen dasselbe
 * Kontingent), und danach merkt sich die Reise seine id. Kein eigener
 * Uploadweg — ein zweiter wäre ein zweites Regelwerk.
 */
export function Symbolbild({ trip }: { trip: Trip }) {
  const texte = useTexte()
  const t = texte.reise.symbolbild
  const upload = useUploadMedia(trip.id)
  const client = useQueryClient()
  const feld = useRef<HTMLInputElement>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [waehlt, setWaehlt] = useState(false)

  const ausschnitt = ausschnittAus(trip.cover_crop)

  /**
   * Nur den Ausschnitt schicken -- **ohne `media_id`**.
   *
   * Der Server lässt das Bild dann stehen. Wer den Ausschnitt verschiebt, soll
   * dasselbe Foto nicht noch einmal hochladen müssen.
   */
  async function sichereAusschnitt(neu: Ausschnitt) {
    setWaehlt(false)
    setProblem(null)
    setLaeuft(true)
    try {
      await api.post<Trip>(`/api/trips/${trip.id}/cover`, neu)
      await client.invalidateQueries()
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? texte.allgemein.ohneNetz
          : fehler instanceof Error
            ? fehler.message
            : texte.allgemein.schiefgelaufen,
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function waehle(datei: File | undefined) {
    if (!datei) return
    setProblem(null)
    setLaeuft(true)
    try {
      const werte = await ensureSettings(client)
      const fertig = await prepareImage(datei, {
        ...(werte ? { maxEdge: werte.image_max_edge, quality: werte.image_quality } : {}),
        reiseVersatz: trip.tz_offset_min ?? null,
      })
      const medium: Media = await upload.mutateAsync({ blob: fertig.blob })
      await api.post<Trip>(`/api/trips/${trip.id}/cover`, { media_id: medium.id })
      // Die Reise steht in beiden Listen -- Übersicht und Reiseangaben.
      await client.invalidateQueries()
    } catch (fehler) {
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? texte.allgemein.ohneNetz
          : fehler instanceof Error
            ? fehler.message
            : t.bildFehlgeschlagen,
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function weg() {
    if (!(await rueckfrage(t.wegFrage, t.wegnehmen))) return
    setProblem(null)
    setLaeuft(true)
    try {
      await api.delete<Trip>(`/api/trips/${trip.id}/cover`)
      await client.invalidateQueries()
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : texte.allgemein.schiefgelaufen)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.titel}</h2>
      <p className="card__lead">{t.lead}</p>

      <div className="symbolbild">
        {trip.cover_url ? (
          <span className="symbolbild__vorschau">
            <img src={trip.cover_url} alt="" style={bildStil(ausschnitt)} />
          </span>
        ) : (
          <span className="symbolbild__leer" aria-hidden="true">
            <PhotoIcon size={22} />
          </span>
        )}

        <div className="symbolbild__knoepfe">
          {/* Hier darf die Dateiauswahl alles anbieten: es ist der Reisende,
              und ein Symbolbild ist genau das, was man gerade fotografiert
              hat oder in den Fotos liegen hat. */}
          <input
            ref={feld}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              void waehle(event.target.files?.[0])
              event.target.value = ''
            }}
          />
          <button
            type="button"
            className="button button--small"
            disabled={laeuft}
            onClick={() => feld.current?.click()}
          >
            {laeuft ? texte.allgemein.laeuft : trip.cover_url ? t.anderesBild : t.bildWaehlen}
          </button>

          {trip.cover_url && (
            <button
              type="button"
              className="button button--small button--outline"
              disabled={laeuft}
              onClick={() => setWaehlt(true)}
            >
              {t.ausschnitt}
            </button>
          )}

          {trip.cover_url && (
            <button
              type="button"
              className="button button--small button--outline"
              disabled={laeuft}
              onClick={() => void weg()}
            >
              <TrashIcon size={14} /> {t.wegnehmen}
            </button>
          )}
        </div>
      </div>

      {/* Keine stille Absage: was nicht durchging, steht als Text da. */}
      {problem && (
        <p className="feld__hinweis" role="alert">
          {problem}
        </p>
      )}

      {/* **Quadratisch und nie kleiner als füllend.** So steht das Symbolbild
          in der Übersicht: in einem runden Feld, das es ganz ausfüllen muss. */}
      {waehlt && trip.cover_url && (
        <Ausschnittwahl
          titel={t.ausschnittTitel}
          bildUrl={trip.cover_url}
          ausschnitt={ausschnitt}
          form={{ breite: 1, hoehe: 1 }}
          kleinste={100}
          onFertig={(neu) => void sichereAusschnitt(neu)}
          onSchliessen={() => setWaehlt(false)}
        />
      )}
    </div>
  )
}
